import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Eye, Trash2, Download, Hand, Lock, PenSquare, Server, Cookie, CheckCircle, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Card from '../components/ui/Card';
import { useSEO } from '../hooks/useSEO';

const rights = [
  { key: 'access', icon: Eye },
  { key: 'deletion', icon: Trash2 },
  { key: 'portability', icon: Download },
  { key: 'objection', icon: Hand },
  { key: 'restriction', icon: Lock },
  { key: 'correction', icon: PenSquare },
];

export default function GdprPolicy() {
  const { t } = useLanguage();
  useSEO({
    title: 'Privacy Policy',
    description:
      'GRAWEO data protection and privacy policy — how we collect, use, and protect your personal data in compliance with GDPR regulations.',
  });

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-graweo-chocolate to-graweo-olive text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-6">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-semibold">{t('gdpr_badge')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold font-manrope mb-6">
              {t('gdpr_title')}
            </h1>
            <p className="text-xl text-gray-100 leading-relaxed">
              {t('gdpr_subtitle')}
            </p>
            <p className="mt-4 text-sm text-white/60">{t('gdpr_updated')}</p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-manrope text-gray-900 mb-6">
            {t('gdpr_intro_title')}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t('gdpr_intro_text')}
          </p>
        </div>
      </section>

      {/* What We Collect */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-manrope text-gray-900 mb-6">
            {t('gdpr_data_title')}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t('gdpr_data_text')}
          </p>
        </div>
      </section>

      {/* Legal Basis */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-manrope text-gray-900 mb-6">
            {t('gdpr_purpose_title')}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line">
            {t('gdpr_purpose_text')}
          </p>
        </div>
      </section>

      {/* Your Rights */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold font-manrope text-gray-900 mb-4">
            {t('gdpr_rights_title')}
          </h2>
          <p className="text-lg text-gray-600 mb-10">
            {t('gdpr_rights_intro')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {rights.map(({ key, icon: Icon }) => (
              <Card key={key} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-graweo-terra/10 rounded-xl flex items-center justify-center">
                    <Icon className="w-5 h-5 text-graweo-terra" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {t(`gdpr_right_${key}`)}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {t(`gdpr_right_${key}_desc`)}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Infrastructure — cross-link to u-cloud24.com */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Server className="w-7 h-7 text-graweo-terra flex-shrink-0" />
            <h2 className="text-3xl font-bold font-manrope text-gray-900">
              {t('gdpr_tech_title')}
            </h2>
          </div>
          <Card className="p-8 border-l-4 border-graweo-terra">
            <p className="text-lg text-gray-600 leading-relaxed mb-4">
              {t('gdpr_tech_text_before')}{' '}
              <a
                href="https://u-cloud24.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-graweo-terra font-semibold hover:underline"
              >
                U-Cloud24
              </a>
              {' '}{t('gdpr_tech_text_after')}
            </p>
            <p className="text-sm text-gray-500">
              {t('gdpr_tech_visit')}{' '}
              <a
                href="https://u-cloud24.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-graweo-terra hover:underline"
              >
                u-cloud24.com
              </a>
            </p>
          </Card>
        </div>
      </section>

      {/* Cookies */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Cookie className="w-7 h-7 text-graweo-terra flex-shrink-0" />
            <h2 className="text-3xl font-bold font-manrope text-gray-900">
              {t('gdpr_cookies_title')}
            </h2>
          </div>
          <p className="text-lg text-gray-600 leading-relaxed">
            {t('gdpr_cookies_text')}
          </p>
        </div>
      </section>

      {/* Consent */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <CheckCircle className="w-7 h-7 text-graweo-terra flex-shrink-0" />
            <h2 className="text-3xl font-bold font-manrope text-gray-900">
              {t('gdpr_consent_title')}
            </h2>
          </div>
          <Card className="p-8 bg-graweo-terra/5 border border-graweo-terra/20">
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('gdpr_consent_text')}
            </p>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Mail className="w-7 h-7 text-graweo-terra flex-shrink-0" />
            <h2 className="text-3xl font-bold font-manrope text-gray-900">
              {t('gdpr_contact_title')}
            </h2>
          </div>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            {t('gdpr_contact_text')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="mailto:info@graweo.org"
              className="inline-flex items-center gap-2 bg-graweo-terra text-white px-6 py-3 rounded-xl font-semibold hover:bg-graweo-terra/90 transition-colors"
            >
              <Mail className="w-4 h-4" />
              info@graweo.org
            </a>
            <Link
              to="/contacts"
              className="inline-flex items-center gap-2 border border-graweo-chocolate text-graweo-chocolate px-6 py-3 rounded-xl font-semibold hover:bg-graweo-chocolate/5 transition-colors"
            >
              {t('gdpr_contact_form_link')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
